import React, { useContext, useMemo, memo, Fragment, CSSProperties } from "react";
import monday from "../tools/monday";
import { Item, Labels } from "../types";
import { TimelineEvent } from "../tools/events";
import { DataContext } from "./DataProvider";
import Tooltip from "../common/Tooltip";
import { formatTimeDiff } from "../tools";
import SignpostArrow from "../common/SignpostArrow";

import "./BoardItem.css";

interface Props {
    item: Item;
    itemId: string;
    event: TimelineEvent;
    labels: Labels;
    movedFrom: string | null | undefined;
    moveDirection: string;
    moveStyle: CSSProperties;
    isPlaying: boolean; // true is animating board - don't execute extra animations
    onJumpToClick: (e: TimelineEvent) => void;
    showPrevNextEvents: boolean;
    showProgress: boolean;
}

const BoardItem: React.FC<Props> = function ({
    item,
    itemId,
    event,
    labels,
    movedFrom,
    moveDirection,
    moveStyle,
    isPlaying,
    onJumpToClick,
    showPrevNextEvents,
    showProgress
}: Props) {
    const { time } = useContext(DataContext);

    const handleItemClick = (): void => {
        monday.execute("openItemCard", { itemId });
    };

    let progress = 0;
    let dt = 0;
    if (event.dt) {
        dt = event.dt;
        progress = time.diff(event.date) / event.dt;
    }
    const percent = Math.ceil(progress * 100);

    const { thisLabel, prevLabel, nextLabel } = useMemo(() => {
        const prevEvent = event?.prevEvent;
        const nextEvent = event?.nextEvent;

        let prevLabel, nextLabel;
        if (prevEvent) {
            prevLabel = {
                name: labels.names[prevEvent.value],
                style: labels.labelsColors[prevEvent.value]
            };
        }
        if (nextEvent) {
            nextLabel = {
                name: labels.names[nextEvent.value],
                style: labels.labelsColors[nextEvent.value]
            };
        }

        const thisLabel = {
            name: labels.names[event.value],
            style: labels.labelsColors[event.value]
        };

        return {
            thisLabel,
            prevLabel,
            nextLabel
        };
    }, [labels, event]);

    const newItem = movedFrom === null;
    const moved = movedFrom !== undefined && !newItem;

    return (
        <div
            className={`board_item ${movedFrom !== undefined ? "modified" : ""}`}
            style={{ backgroundColor: thisLabel.style.color }}
            onClick={handleItemClick}
        >
            <div
                className={`board_item__card_container ${
                    !isPlaying && (moved || newItem) ? `in-flight-${moveDirection}` : ""
                }`}
            >
                <div
                    className={`board_item__card ${moved ? "in-flight" : ""} ${newItem ? "new" : ""}`}
                    style={moved ? moveStyle : undefined}
                >
                    {showPrevNextEvents ? (
                        <div className="card__prev_next">
                            {prevLabel ? (
                                <SignpostArrow
                                    text={prevLabel.name}
                                    arrowColor={prevLabel.style.color}
                                    backgroundColor="var(--secondary-background-color)"
                                    className="card__prev_next__arrow"
                                    type="left"
                                    onClick={(): void => onJumpToClick(event.prevEvent!)}
                                />
                            ) : (
                                <div className="card__prev_next__arrow" />
                            )}
                            {nextLabel ? (
                                <Fragment>
                                    <div className="card__prev_next__separator" />
                                    <SignpostArrow
                                        text={nextLabel.name}
                                        arrowColor={nextLabel.style.color}
                                        backgroundColor="var(--secondary-background-color)"
                                        className="card__prev_next__arrow"
                                        type="right"
                                        onClick={(): void => onJumpToClick(event.nextEvent!)}
                                    />
                                </Fragment>
                            ) : null}
                        </div>
                    ) : null}

                    <div className="card__text">{item.name}</div>

                    {showProgress && percent > 0 && (
                        <div className="card__progress tooltip">
                            <Tooltip
                                className="board_item_tooltip"
                                text={`${percent}% of ${formatTimeDiff(dt, true)} in ${thisLabel.name}`}
                            />
                            <div
                                className="card__progress__bar"
                                style={{ backgroundColor: thisLabel.style.colorInactive, width: `${percent}%` }}
                            >
                                {percent >= 12 ? `${percent}% ` : ""}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

export default memo(BoardItem);
